import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api } from '@/api/client';
import { PageHeader } from '@/components/ui/PageHeader';
import { cn } from '@/lib/utils';

/**
 * Техническая страница: что на платформе живо, а что нет.
 *
 * ⚠️ ЗАЧЕМ ОНА. Когда у ученика что-то не работает, первый вопрос — «это у него
 * или у нас?». Без этой страницы ответ добывался входом на сервер и чтением
 * журналов; теперь он виден за пять секунд, и получить его может не только тот,
 * у кого есть доступ к серверу.
 *
 * ⚠️ ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ. Журналов служб здесь нет намеренно: чтобы читать
 * их из приложения, пришлось бы дать ему доступ к управлению контейнерами — а
 * это ключ от всего сервера, выданный ради удобства. Журналы читаются на
 * сервере, где им и место.
 */

type ServiceHealth = {
  name: string;
  status: 'ok' | 'slow' | 'down';
  latency: number;
  detail?: string;
};

type PlatformHealth = {
  services: ServiceHealth[];
  database: ServiceHealth;
  checked_at: string;
  ok: number;
  down: number;
};

const DOT: Record<string, string> = {
  ok: 'bg-success',
  slow: 'bg-warning',
  down: 'bg-error',
};

const TEXT: Record<string, string> = {
  ok: 'text-success',
  slow: 'text-warning',
  down: 'text-error',
};

function Row({ s }: { s: ServiceHealth }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 border-b border-outline py-3 last:border-b-0">
      <span className={cn('h-2.5 w-2.5 flex-none rounded-full', DOT[s.status] ?? DOT.down)} />
      <span className="min-w-0 flex-1 text-[13.5px] font-semibold text-on-surface">{s.name}</span>
      {s.detail && <span className="flex-none text-[12px] text-on-surface-variant">{s.detail}</span>}
      <span className={cn('flex-none text-[12.5px] font-bold tabular-nums', TEXT[s.status] ?? TEXT.down)}>
        {s.status === 'down' ? t('platformHealth.statusDown') : `${s.latency} ms`}
      </span>
    </div>
  );
}

export function AdminPlatformHealthPage() {
  const { t } = useTranslation();

  /**
   * ⚠️ ОБНОВЛЯЕМ САМИ, РАЗ В ПЯТНАДЦАТЬ СЕКУНД. На эту страницу смотрят, когда
   * что-то чинят, и жать «обновить» в такой момент — лишнее движение. Чаще не
   * нужно: опрос идёт по всем шестнадцати службам сразу.
   */
  const health = useQuery({
    queryKey: ['admin', 'platform-health'],
    queryFn: async () => {
      const { data } = await api.get('/admin/platform/health');
      return data as PlatformHealth;
    },
    refetchInterval: 15_000,
  });

  const d = health.data;
  const allAlive = d ? d.down === 0 : false;

  return (
    <>
      <PageHeader title={t('platformHealth.title')} subtitle={t('platformHealth.subtitle')} />

      {health.isLoading && (
        <p className="py-6 text-center text-[13px] text-on-surface-variant">
          {t('platformHealth.loading')}
        </p>
      )}

      {health.isError && (
        <div className="rounded-[20px] bg-error-container px-[18px] py-4 text-[13px] text-on-error-container">
          {t('platformHealth.selfError')}
        </div>
      )}

      {d && (
        <>
          {/* Общий вывод крупно: ради него страницу и открывают. */}
          <div
            className={cn(
              'mb-3.5 rounded-[22px] px-[22px] py-[18px]',
              allAlive ? 'bg-success-container' : 'bg-error-container',
            )}
          >
            <div
              className={cn(
                'font-display text-[22px] font-bold',
                allAlive ? 'text-on-success-container' : 'text-on-error-container',
              )}
            >
              {allAlive
                ? t('platformHealth.allOk')
                : t('platformHealth.someDown', { count: d.down })}
            </div>
            <div
              className={cn(
                'mt-1 text-[13px]',
                allAlive ? 'text-on-success-container' : 'text-on-error-container',
              )}
            >
              {t('platformHealth.summary', {
                ok: d.ok,
                time: new Date(d.checked_at).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                }),
              })}
            </div>
          </div>

          <div className="rounded-[20px] bg-surface-container px-[18px] py-0.5">
            <Row s={d.database} />
            {d.services.map((s) => (
              <Row key={s.name} s={s} />
            ))}
          </div>

          <p className="mt-3 px-1 text-[12px] leading-[1.5] text-on-surface-variant">
            {t('platformHealth.slowNote')}
          </p>
        </>
      )}
    </>
  );
}
